import { Box, Button, CircularProgress, Typography } from '@mui/material'
import CustomDialog from '../CustomDialog'
import { ConvertFailedErrorType } from '../../models/File'
import { useCallback, useState } from 'react'
import { ConvertFailedIcon } from '../Svgs'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '../../store'
import { deleteFileAsync } from '../../services/filesService'
import { sendEmailAsync } from '../../services/notificationService'
import { NotificationType } from '../../models/Notification'

const projectsSelector = createSelector(
  [(state: RootState) => state.organization],
  (organization) => organization.projects,
)
const selectedFileSelector = createSelector(
  [(state: RootState) => state.organization],
  (organization) => organization.selectedFile,
)

interface ConfirmReassignProps {
  isOpen: boolean
  errorType: ConvertFailedErrorType
  handleClose: () => void
}

const ConvertFailedModalComponent: React.FC<ConfirmReassignProps> = ({
  isOpen,
  errorType,
  handleClose,
}) => {
  const dispatch = useDispatch<any>()
  const navigate = useNavigate()
  const selectedFile = useSelector(selectedFileSelector)
  const projects = useSelector(projectsSelector)
  const project = projects.find(
    (project) => project.id === selectedFile?.project_id,
  )
  const [actionTriggered, setActionTriggered] = useState(false)

  const handleContinueClick = () => {
    if (selectedFile) {
      setActionTriggered(true)
      dispatch(
        sendEmailAsync({
          file: selectedFile,
          url: `${window.location.origin}/projects/${project?.id}/files/${selectedFile.id}`,
          notificationType: NotificationType.FileNotification,
        }),
      )
      navigate(-1)
    }
  }

  const handleDeleteClick = async () => {
    if (selectedFile) {
      setActionTriggered(true)
      dispatch(
        sendEmailAsync({
          file: selectedFile,
          url: `${window.location.origin}/projects/${project?.id}/files/${selectedFile.id}`,
          notificationType: NotificationType.FileDeletion,
        }),
      )
      await dispatch(deleteFileAsync({ fileId: selectedFile?.id! })).unwrap()
      navigate(-1)
    }
  }

  const renderContent = useCallback(() => {
    switch (errorType) {
      case ConvertFailedErrorType.SystemError:
        return (
          <Box
            sx={{
              display: 'flex',
              padding: '1rem 2rem',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '2rem',
              maxWidth: '24rem',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1.25rem',
                justifyContent: 'center',
                alignItems: 'center',
                px: '1.5rem',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <ConvertFailedIcon
                  sx={{ fontSize: '6rem', color: '#56003D' }}
                />
                <Typography
                  sx={{
                    color: '#06004A',
                    fontSize: '1rem',
                    lineHeight: 'normal',
                    fontWeight: 600,
                  }}
                >
                  Hmm, we need a little more time…
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.15rem',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    sx={{
                      color: '#4F4F4F',
                      fontSize: '0.75rem',
                      lineHeight: 'normal',
                    }}
                  >
                    This floor plan needs a bit more processing.
                  </Typography>
                  <Typography
                    sx={{
                      color: '#4F4F4F',
                      fontSize: '0.75rem',
                      lineHeight: 'normal',
                    }}
                  >
                    No worries — it’s happening in the background.
                  </Typography>
                </Box>
              </Box>
              <Typography
                sx={{
                  color: '#4F4F4F',
                  fontSize: '0.75rem',
                  lineHeight: 'normal',
                  textAlign: 'center',
                }}
              >
                You’ll receive the project by email once it’s ready.
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: '0.75rem',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <Button
                disableRipple
                disabled={actionTriggered}
                onClick={handleContinueClick}
                sx={{
                  background: '#1705E5',
                  color: '#FFFFFF',
                  borderRadius: '0.4rem',
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  width: '70%',
                  minHeight: '2.1rem',
                }}
              >
                {actionTriggered ? (
                  <CircularProgress size={20} sx={{ color: '#FFFFFF' }} />
                ) : (
                  'Okay, back to project files'
                )}
              </Button>
              <Button
                disableRipple
                disabled={actionTriggered}
                onClick={handleDeleteClick}
                sx={{
                  background: '#04002E',
                  color: '#FFFFFF',
                  borderRadius: '0.4rem',
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  width: '30%',
                  minHeight: '2.1rem',
                }}
              >
                {actionTriggered ? (
                  <CircularProgress size={20} sx={{ color: '#FFFFFF' }} />
                ) : (
                  'Delete'
                )}
              </Button>
            </Box>
          </Box>
        )
    }
  }, [errorType, actionTriggered])

  return (
    <CustomDialog
      isOpen={isOpen}
      title=''
      handleClose={handleClose}
      hideActions={true}
      hideCloseButton={true}
    >
      {renderContent()}
    </CustomDialog>
  )
}

export default ConvertFailedModalComponent
